import { PureComponent, ReactNode } from 'react';
import { Input, Table } from 'antd';
const Column = Table.Column;

import TableWithDragableRows, { DragHandle } from '../padV2/TableWithDragableRows';
import { GeniePadElementsSettingItem } from '../../../../../api/pushGenie';
import { arrayMoveImmutable } from 'array-move';
import { columnHeaderContent, headerStyle } from '../../../../common/TableUtils';
import { EkaProIcon } from '../../../../common/Icons';
import { WHITELISTED_EDIT_SECTION_NAME_KEYS, DENTAL_CHART } from '../../../../../constants';
import { SectionNameConfig } from '../../../../../store/db';
import { AntTooltip } from '../../../../common/AntTooltip';
import { getSectionNameFromKey } from '../../../../../pages/utils';

interface PxElementsConfigureTableProps<T> {
    onUpdate: (config: T[]) => void;
    alternateSectionNameConfig?: SectionNameConfig;
    onSectionNameEditName?: (sectionName: string, name: string) => void;
    onSectionNameEdit?: (sectionName?: string) => void;
    isSectionNameEdit?: string;
    config: T[];
    isDragHandleEnabled?: boolean;
    disableRowBasedOnKey?: keyof T;
    customColumnConfig?: {
        title: string;
        keyName: keyof T;
    }[];
}

class ElementsConfigureTable<T extends GeniePadElementsSettingItem> extends PureComponent<
    PxElementsConfigureTableProps<T>
> {
    componentHeaderContent = columnHeaderContent('Field');
    shownHeaderContent = columnHeaderContent('Enable / Disable');

    // TODO, check if also can be reused across
    rowKeyFromId = (id: string, index?: number): string =>
        `${id}-${index}-${index !== undefined && this.props.config[index].isShown}`;

    findIndexForRecordKey = (key: string): number => {
        return this.props.config.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    renderComponent = (keyName: keyof T) => {
        return (_: T, record: T): { children: JSX.Element } => {
            const {
                isDragHandleEnabled = true,
                onSectionNameEditName,
                onSectionNameEdit,
                isSectionNameEdit,
                alternateSectionNameConfig,
            } = this.props;

            return {
                children: (
                    <div className="template-list flex space-x-16 items-center">
                        {isDragHandleEnabled && record[keyName] && <DragHandle />}

                        {WHITELISTED_EDIT_SECTION_NAME_KEYS?.find(
                            (section) => section === record.id,
                        ) &&
                        onSectionNameEdit &&
                        onSectionNameEditName ? (
                            isSectionNameEdit === record.id ? (
                                <>
                                    {isSectionNameEdit === 'followup-advices' ? (
                                        <div className="flex justify-between items-center rounded-t-8">
                                            <div className="space-x-4 bg-white flex items-center justify-between px-8 py-1 max-w-462 w-full mr-8">
                                                <Input
                                                    autoFocus
                                                    className="w-full font-700 placeholder-neutral-6 text-neutral-black text-14 rounded-6 border border-seperator-dark"
                                                    defaultValue={
                                                        alternateSectionNameConfig?.['followup'] ||
                                                        'Follow Up'
                                                    }
                                                    onChange={(e) => {
                                                        onSectionNameEditName(
                                                            'followup',
                                                            e.target.value,
                                                        );
                                                    }}
                                                    onBlur={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                    onPressEnter={() => {
                                                        onSectionNameEdit(undefined);
                                                    }}
                                                    size="small"
                                                />{' '}
                                                <div className="font-700"> & </div>{' '}
                                                <Input
                                                    className="w-full font-700 placeholder-neutral-6 text-neutral-black text-14 rounded-6 border border-seperator-dark"
                                                    defaultValue={
                                                        alternateSectionNameConfig?.['advices'] ||
                                                        'Advices'
                                                    }
                                                    onChange={(e) => {
                                                        onSectionNameEditName(
                                                            'advices',
                                                            e.target.value,
                                                        );
                                                    }}
                                                    onBlur={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                    onPressEnter={() => {
                                                        onSectionNameEdit(undefined);
                                                    }}
                                                    size="small"
                                                />
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-green-800 cursor-pointer"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 448 512"
                                                    onClick={() => {
                                                        onSectionNameEdit(undefined);
                                                    }}
                                                >
                                                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                                                </svg>
                                                <svg
                                                    className="w-16 h-16 fill-darwin-status-warning-500 cursor-pointer"
                                                    viewBox="0 0 9 8"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => {
                                                        onSectionNameEdit(undefined);
                                                    }}
                                                >
                                                    <path d="M7.72266 6.15234C8.00195 6.41016 8.00195 6.86133 7.72266 7.11914C7.59375 7.24805 7.42188 7.3125 7.25 7.3125C7.05664 7.3125 6.88477 7.24805 6.75586 7.11914L4.5 4.86328L2.22266 7.11914C2.09375 7.24805 1.92188 7.3125 1.75 7.3125C1.55664 7.3125 1.38477 7.24805 1.25586 7.11914C0.976562 6.86133 0.976562 6.41016 1.25586 6.15234L3.51172 3.875L1.25586 1.61914C0.976562 1.36133 0.976562 0.910156 1.25586 0.652344C1.51367 0.373047 1.96484 0.373047 2.22266 0.652344L4.5 2.9082L6.75586 0.652344C7.01367 0.373047 7.46484 0.373047 7.72266 0.652344C8.00195 0.910156 8.00195 1.36133 7.72266 1.61914L5.4668 3.89648L7.72266 6.15234Z"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-center rounded-t-8">
                                            <div className="bg-white flex items-center justify-between px-8 py-1 max-w-462 w-full mr-8">
                                                <Input
                                                    autoFocus
                                                    className="w-full font-700 placeholder-neutral-6 text-neutral-black text-14 rounded-6 border border-seperator-dark"
                                                    defaultValue={
                                                        alternateSectionNameConfig?.[record.id] ||
                                                        record.name
                                                    }
                                                    onChange={(e) => {
                                                        onSectionNameEditName(
                                                            record.id,
                                                            e.target.value,
                                                        );
                                                    }}
                                                    onBlur={(e) => {
                                                        if (e.target.value) {
                                                            onSectionNameEdit(undefined);
                                                        } else {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onPressEnter={() => {
                                                        onSectionNameEdit(undefined);
                                                    }}
                                                    size="small"
                                                />
                                            </div>
                                            <svg
                                                className="w-16 h-16 fill-darwin-status-warning-500 cursor-pointer"
                                                viewBox="0 0 9 8"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                onClick={() => {
                                                    onSectionNameEdit(undefined);
                                                }}
                                            >
                                                <path d="M7.72266 6.15234C8.00195 6.41016 8.00195 6.86133 7.72266 7.11914C7.59375 7.24805 7.42188 7.3125 7.25 7.3125C7.05664 7.3125 6.88477 7.24805 6.75586 7.11914L4.5 4.86328L2.22266 7.11914C2.09375 7.24805 1.92188 7.3125 1.75 7.3125C1.55664 7.3125 1.38477 7.24805 1.25586 7.11914C0.976562 6.86133 0.976562 6.41016 1.25586 6.15234L3.51172 3.875L1.25586 1.61914C0.976562 1.36133 0.976562 0.910156 1.25586 0.652344C1.51367 0.373047 1.96484 0.373047 2.22266 0.652344L4.5 2.9082L6.75586 0.652344C7.01367 0.373047 7.46484 0.373047 7.72266 0.652344C8.00195 0.910156 8.00195 1.36133 7.72266 1.61914L5.4668 3.89648L7.72266 6.15234Z"></path>
                                            </svg>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex justify-between items-center rounded-t-8">
                                    <div className="flex items-center w-full space-x-8">
                                        <p className="body-1 bold text-darwin-neutral-1000 cursor-pointer">
                                            {getSectionNameFromKey(
                                                record.id,
                                                alternateSectionNameConfig,
                                            ) || record.name}
                                        </p>
                                        <div className="delete-icon flex items-center justify-center space-x-8 cursor-pointer">
                                            <AntTooltip content="Edit Name">
                                                <svg
                                                    onClick={() => {
                                                        onSectionNameEdit(record.id);
                                                    }}
                                                    className="w-16 h-16 fill-neutral-1"
                                                    role="img"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M495.6 49.23l-32.82-32.82C451.8 5.471 437.5 0 423.1 0c-14.33 0-28.66 5.469-39.6 16.41L167.5 232.5C159.1 240 154.8 249.5 152.4 259.8L128.3 367.2C126.5 376.1 133.4 384 141.1 384c.916 0 1.852-.0918 2.797-.2813c0 0 74.03-15.71 107.4-23.56c10.1-2.377 19.13-7.459 26.46-14.79l217-217C517.5 106.5 517.4 71.1 495.6 49.23zM461.7 94.4L244.7 311.4C243.6 312.5 242.5 313.1 241.2 313.4c-13.7 3.227-34.65 7.857-54.3 12.14l12.41-55.2C199.6 268.9 200.3 267.5 201.4 266.5l216.1-216.1C419.4 48.41 421.6 48 423.1 48s3.715 .4062 5.65 2.342l32.82 32.83C464.8 86.34 464.8 91.27 461.7 94.4zM424 288c-13.25 0-24 10.75-24 24v128c0 13.23-10.78 24-24 24h-304c-13.22 0-24-10.77-24-24v-304c0-13.23 10.78-24 24-24h144c13.25 0 24-10.75 24-24S229.3 64 216 64L71.1 63.99C32.31 63.99 0 96.29 0 135.1v304C0 479.7 32.31 512 71.1 512h303.1c39.69 0 71.1-32.3 71.1-72L448 312C448 298.8 437.3 288 424 288z"></path>
                                                </svg>
                                            </AntTooltip>
                                            <AntTooltip content="Reset to Original Name">
                                                <svg
                                                    onClick={() => {
                                                        if (record.id === 'followup-advices') {
                                                            onSectionNameEditName('followup', '');
                                                            onSectionNameEditName('advices', '');
                                                        } else {
                                                            onSectionNameEditName(record.id, '');
                                                        }
                                                    }}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    className="w-16 h-16 fill-neutral-1"
                                                >
                                                    <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z" />
                                                </svg>
                                            </AntTooltip>
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : (
                            <p className="body-1 bold text-darwin-neutral-1000">{record.name}</p>
                        )}
                        <div className="flex items-center space-x-8">
                            {record.id === DENTAL_CHART && (
                                <div className="px-8 py-2 font-700 text-center body-3 bg-darwin-accent-medical-history-orange-800 rounded-4 text-white">
                                    New
                                </div>
                            )}
                            {record.id === DENTAL_CHART && (
                                <EkaProIcon className="w-41 h-20" id="dental-chart-pro-icon" />
                            )}
                        </div>
                    </div>
                ),
            };
        };
    };

    toggleShown = (keyName: keyof T, index: number): void => {
        const { disableRowBasedOnKey, customColumnConfig } = this.props;

        const configClone = Array.from(this.props.config);

        if (keyName === disableRowBasedOnKey && !configClone[index][keyName] === false) {
            const rowConf = customColumnConfig?.reduce((prevObj, conf) => {
                return {
                    ...prevObj,
                    [conf.keyName]: false,
                };
            }, {});
            configClone[index] = {
                ...configClone[index],
                ...rowConf,
                [keyName]: !configClone[index][keyName],
            };
        } else {
            configClone[index] = { ...configClone[index], [keyName]: !configClone[index][keyName] };
        }

        this.props.onUpdate(configClone);
    };

    onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        const { config } = this.props;
        const newData = arrayMoveImmutable([...config], oldIndex, newIndex).filter((el) => !!el);

        this.props.onUpdate(newData);
    };

    renderShown = (keyName: keyof T) => {
        return (
            _: GeniePadElementsSettingItem,
            __: GeniePadElementsSettingItem,
            index: number,
        ): { children: JSX.Element } => {
            const { disableRowBasedOnKey } = this.props;
            const record = this.props?.config[index];
            return {
                children: (
                    <div
                        onClick={() => this.toggleShown(keyName, index)}
                        className={`flex items-center justify-center w-full cursor-pointer ${
                            record.id === 'milestones' ||
                            (disableRowBasedOnKey &&
                                !record[disableRowBasedOnKey] &&
                                keyName !== disableRowBasedOnKey)
                                ? 'pointer-events-none opacity-50'
                                : ''
                        }`}
                    >
                        {record[keyName] ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-status-success"
                            >
                                <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"></path>
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-neutral-6"
                            >
                                <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                            </svg>
                        )}
                    </div>
                ),
            };
        };
    };
    render(): ReactNode {
        const { config, customColumnConfig, alternateSectionNameConfig } = this.props;

        return (
            <TableWithDragableRows
                id="pad-config-table"
                showHeader
                tableLayout="fixed"
                className="pad-config-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={config}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={this.onDrag}
            >
                <Column
                    title={this.componentHeaderContent}
                    onHeaderCell={headerStyle}
                    key="pad-config-row-component"
                    className="w-50p"
                    render={this.renderComponent('isShown')}
                />
                <Column
                    title={this.shownHeaderContent}
                    onHeaderCell={headerStyle}
                    key="pad-config-row-shown"
                    className="w-25p"
                    render={this.renderShown('isShown')}
                />
                {customColumnConfig?.map((conf) => {
                    return (
                        <Column
                            title={columnHeaderContent(conf.title)}
                            onHeaderCell={headerStyle}
                            key="pad-config-row-shown"
                            className="w-25p"
                            render={this.renderShown(conf.keyName)}
                        />
                    );
                })}
            </TableWithDragableRows>
        );
    }
}

export default ElementsConfigureTable;
